/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import './fonts.css';
import './breakpoints.css';

import type { EditorType } from '@ckeditor/ckeditor5-metadata';
import { show } from '@ebay/nice-modal-react';
import { Box } from '@mui/material';
import { type ReactNode, useContext, useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';

import { useMemoizedState } from '../../hooks/useMemoizedState/useMemoizedState';
import { useLocationSynchronization } from '../../hooks/useLocationSynchronization/useLocationSynchronization';

import { tabs } from '../../constants/tabs';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { type PresetView } from '../../utils/getPresets/getPresets';
import { DrawerLeft } from '../DrawerLeft/DrawerLeft';
import { defaultLanguage, type EditorLanguage } from '../LanguageSelection/languages';
import type { CapabilityParsed, PluginSelectionMode } from '../PluginSelection/types';
import { TopBar } from '../TopBar/TopBar';
import type { CKEditor5BuilderProps } from './types';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import { getPluginsToSelect } from '../../utils/getPluginsToSelect/getPluginsToSelect';
import { getPluginsToDeselect } from '../../utils/getPluginsToDeselect/getPluginsToDeselect';
import { plugins } from '../../constants/plugins';
import { capabilities, type Capability } from '../../constants/capabilities';
import { GTMContext } from '../../contexts/GTMContext';
import { defaultToolbarOptions } from '../../constants/toolbars';
import { EditorPreviewPlaceholder } from '../EditorPreviewPlaceholder/EditorPreviewPlaceholder';
import {
	GTM_EVENT_SELECT_PRESET,
	GTM_EVENT_SIDEBAR_COLLAPSE,
	GTM_EVENT_SIDEBAR_UNCOLLAPSE,
	GTM_EVENT_TOGGLE_VIEW
} from '../../constants/GTMEventNames';
import { removeDuplicateStrings } from '../../utils/removeDuplicateStrings/removeDuplicateStrings';
import { useHotJarStylesRelay } from '../../hooks/useHotJarStylesRelay/useHotJarStylesRelay';
import { useUrlTracker } from '../../hooks/useUrlTracker/useUrlTracker';
import type { CodeOutput, InstallMethod, Technology } from '../../constants/installation';
import { InstallationSettingsContext } from '../../contexts/InstallationSettingsContext';
import { EditorSettingsContext } from '../../contexts/EditorSettingsContext';
import { getPluginByClassName } from '../../utils/getPluginByClassName/getPluginByClassName';
import { CustomerPortalContext } from '../../contexts/CustomerPortalContext';

const MANDATORY_PLUGINS = plugins.filter( plugin => plugin.mandatory ).map( plugin => plugin.className );
const APP_BAR_HEIGHT = 'var(--app-bar-height)';

export function CKEditor5Builder( { locationOrigin, previewLocation }: CKEditor5BuilderProps ): ReactNode {
	const { triggerGTMEvent, shouldEmitConversionEvent } = useContext( GTMContext );
	const { emitCustomerPortalEvent, isLoggedIn } = useContext( CustomerPortalContext );

	// See: #833.
	useHotJarStylesRelay();

	// See: #945.
	useUrlTracker();

	// The application layout.
	const [ isSidebarOpen, setIsSidebarOpen ] = useState( true );
	const [ activeTabIndex, setActiveTabIndex ] = useState( 0 );
	const activeTab = tabs.find( tab => activeTabIndex === tab.index )!;

	// The "Presets" view.
	const [ selectedPreset, setSelectedPreset ] = useState<PresetView | null>( null );
	const [ isSelectedPresetModified, setIsSelectedPresetModified ] = useState( false );

	// The "Features" view.
	const [ pluginSelectionView, setPluginSelectionView ] = useState<PluginSelectionMode>( 'basic' );
	const [ localCapabilities, setLocalCapabilities ] = useState<ReadonlyArray<Capability>>( capabilities );

	// The editor properties act as a key for the editor container. To avoid unnecessary re-renderings,
	// store updated values only if they have been changed.
	//
	// See #683, #684.
	const [ editorLanguage, setEditorLanguage ] = useMemoizedState<EditorLanguage>( defaultLanguage );
	const [ toolbarOptions, setToolbarOptions ] = useMemoizedState( defaultToolbarOptions );
	const [ editorType, setEditorType ] = useMemoizedState<EditorType>( 'ClassicEditor' );
	const [ editorPlugins, setEditorPlugins ] = useMemoizedState<EditorPlugins>( [] );

	// State of settings on installation tab.
	const [ selectedTechnology, setSelectedTechnology ] = useState<Technology>( null );
	const [ selectedInstallMethod, setSelectedInstallMethod ] = useState<InstallMethod>( null );
	const [ selectedCodeOutput, setSelectedCodeOutput ] = useState<CodeOutput>( null );

	// See https://github.com/ckeditor/ckeditor5-builder/pull/1239#pullrequestreview-2632255963
	const [ builderUrl, setBuilderUrl ] = useState<string>( '' );

	// See: #56.
	useLocationSynchronization( {
		selectedPreset,
		editorPlugins,
		editorLanguage,
		editorType,
		toolbarOptions,
		activeTabIndex,
		onEditorLanguageChange: setEditorLanguage,
		onToolbarOptionsChange: setToolbarOptions,
		onEditorTypeChange: setEditorType,
		onEditorPluginsChange: setEditorPlugins,
		onPresetChange: setSelectedPreset,
		onIsSelectedPresetModifiedChange: setIsSelectedPresetModified,
		onActiveTabIndexChange: setActiveTabIndex,
		onLocalCapabilitiesChange: setLocalCapabilities,
		onBuilderUrlChange: setBuilderUrl
	} );

	const {
		MainComponent: MainView,
		SidebarComponent: SidebarView
	} = activeTab;

	function handleToggleSidebar() {
		setIsSidebarOpen( previousValue => !previousValue );

		triggerGTMEvent( {
			event: isSidebarOpen ?
				GTM_EVENT_SIDEBAR_COLLAPSE :
				GTM_EVENT_SIDEBAR_UNCOLLAPSE
		} );
	}

	function handleSelectedPresetClick( newPreset: PresetView ) {
		if ( isSelectedPresetModified ) {
			return show( ConfirmationModal, {
				title: 'Are you sure you want to change preset?',
				description: 'Changes made to the current preset will be irretrievably lost.',
				acceptButtonText: 'Change preset',
				declineButtonText: 'Keep my setup',
				onAccept: () => {
					changePreset( newPreset );
				}
			} );
		}

		changePreset( newPreset );
		setSelectedCodeOutput( null );
	}

	function handleEditorPluginsSelect( pluginsToAdd: EditorPlugins ) {
		const pluginsToSelect = getPluginsToSelect( editorPlugins, pluginsToAdd, editorType );
		const calculatedPlugins = getCalculatedPlugins( pluginsToSelect, toolbarOptions );
		const hasPremiumPluginToAdd = pluginsToAdd
			.map( getPluginByClassName )
			.some( plugin => plugin.premium );

		setEditorPlugins( calculatedPlugins );
		setIsSelectedPresetModified( true );

		if ( hasPremiumPluginToAdd ) {
			setSelectedCodeOutput( null );
		}
	}

	function handleEditorPluginsDeselect( pluginsToRemove: EditorPlugins ) {
		const pluginsToDeselect = getPluginsToDeselect( editorPlugins, pluginsToRemove );
		const calculatedPlugins = getCalculatedPlugins( pluginsToDeselect, toolbarOptions );

		setEditorPlugins( calculatedPlugins );
		setIsSelectedPresetModified( true );
	}

	function handlePluginSelectionViewChange( newSelectionView: PluginSelectionMode ) {
		triggerGTMEvent( {
			event: GTM_EVENT_TOGGLE_VIEW,
			action: newSelectionView
		} );

		setPluginSelectionView( newSelectionView );
	}

	function handleToolbarOptionsChange( newPartialToolbarOptions: Partial<ToolbarOptions> ) {
		const newToolbarOptions = { ...toolbarOptions, ...newPartialToolbarOptions };
		const calculatedPlugins = getCalculatedPlugins( editorPlugins, newToolbarOptions );

		setToolbarOptions( newToolbarOptions );
		setEditorPlugins( calculatedPlugins );
		setIsSelectedPresetModified( true );
	}

	function handlePluginToggleChange( group: CapabilityParsed ) {
		let pluginsToAdd: EditorPlugins;

		// When toggling an already selected group, keep it selected.
		// It needs a separate handler because the number of features behind the toggle might be non-equal.
		if ( group.isSelected ) {
			pluginsToAdd = group.toggleFeatures!
				.filter( feature => {
					const toggleFeature = group.features.find( toggleFeature => {
						return toggleFeature.name === feature.toggleFeatureName;
					} );

					// A feature is not available in the toggled group, so it should be picked up.
					if ( !toggleFeature ) {
						return true;
					}

					// But features that are filtered out (due to search results) should be ignored.
					return !toggleFeature.isFiltered;
				} )
				.flatMap( feature => feature.plugins );
		}
		// When toggling a partially selected group, map the features chosen to their toggled equivalent and keep them selected.
		else if ( group.isSelectedIndeterminate ) {
			pluginsToAdd = group.features
				// Process the selected features...
				.filter( feature => feature.isSelected )
				// ...and only if it has its toggled equivalent.
				.filter( feature => feature.toggleFeatureName )
				// Find the toggled feature.
				.map( feature => {
					return group.toggleFeatures!.find( toggleFeature => toggleFeature.name === feature.toggleFeatureName )!;
				} )
				.flatMap( feature => feature.plugins );
		}

		if ( pluginsToAdd! ) {
			const pluginsToDeselect: EditorPlugins = group.plugins.map( plugin => plugin.className )
				// Do not remove plugins that would be selected in the next step to avoid messing up with dependencies.
				.filter( pluginName => !pluginsToAdd.includes( pluginName ) );

			const pluginsAfterDeselecting = getPluginsToDeselect( editorPlugins, pluginsToDeselect );
			const pluginsAfterSelecting = getPluginsToSelect( pluginsAfterDeselecting, pluginsToAdd, editorType );
			const calculatedPlugins = getCalculatedPlugins( pluginsAfterSelecting, toolbarOptions );

			setEditorPlugins( calculatedPlugins );
			setIsSelectedPresetModified( true );
		}

		setLocalCapabilities( previousCapabilities => {
			return previousCapabilities.map( capability => {
				if ( capability.name !== group.name ) {
					return capability;
				}

				const newCapability = cloneDeep( capability );

				newCapability.toggleValue = !newCapability.toggleValue;
				[ newCapability.features, newCapability.toggleFeatures ] = [ newCapability.toggleFeatures!, newCapability.features ];

				return newCapability;
			} );
		} );
	}

	useEffect( () => {
		if ( isLoggedIn ) {
			emitCustomerPortalEvent( { type: 'stage', stage: 'Builder Setup Started' } );
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ isLoggedIn ] );

	useEffect( () => {
		shouldEmitConversionEvent.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ editorLanguage, selectedPreset, editorPlugins, toolbarOptions, selectedTechnology, selectedInstallMethod, selectedCodeOutput ] );

	function changePreset( newPreset: PresetView ) {
		const calculatedPlugins = getCalculatedPlugins( newPreset.plugins, newPreset.toolbarOptions );

		setEditorPlugins( calculatedPlugins );
		setToolbarOptions( newPreset.toolbarOptions );
		setSelectedPreset( newPreset );
		setEditorType( newPreset.editorType );
		setIsSelectedPresetModified( false );

		// #470.
		setEditorLanguage( defaultLanguage );
		// #487.
		setLocalCapabilities( capabilities );

		triggerGTMEvent( {
			event: GTM_EVENT_SELECT_PRESET,
			option: newPreset.name,
			isDirty: isSelectedPresetModified
		} );
	}

	return (
		<EditorSettingsContext.Provider value={ {
			editorType,
			onEditorTypeChange: setEditorType,
			editorLanguage,
			onEditorLanguageChange: setEditorLanguage,
			editorPlugins,
			onEditorPluginsSelect: handleEditorPluginsSelect,
			onEditorPluginsDeselect: handleEditorPluginsDeselect,
			capabilities: localCapabilities,
			onCapabilitiesChange: setLocalCapabilities,
			preset: selectedPreset,
			onPresetChange: handleSelectedPresetClick,
			toolbarOptions,
			onToolbarOptionsChange: handleToolbarOptionsChange,
			isSelectedPresetModified,
			builderUrl
		} }
		>
			<InstallationSettingsContext.Provider value={ {
				selectedTechnology,
				onSelectedTechnologyChange: setSelectedTechnology,
				selectedInstallMethod,
				onSelectedInstallMethodChange: setSelectedInstallMethod,
				selectedCodeOutput,
				onSelectedCodeOutputChange: setSelectedCodeOutput
			} }
			>
				<Box sx={ { backgroundColor: 'grey.200', minHeight: `calc(100vh - ${ APP_BAR_HEIGHT })` } }>
					<TopBar
						activeTabIndex={ activeTabIndex }
						onTabChange={ setActiveTabIndex }
						isSelectedPresetModified={ isSelectedPresetModified }
						appBarHeight={ APP_BAR_HEIGHT }
						pluginSelectionView={ pluginSelectionView }
						isNavigationDisabled={ selectedPreset === null }
					/>

					<DrawerLeft
						isSidebarOpen={ isSidebarOpen }
						activeTab={ activeTab }
						marginTop={ APP_BAR_HEIGHT }
						onToggleSidebar={ handleToggleSidebar }
						onTabChange={ setActiveTabIndex }
						featuresView={ pluginSelectionView }
						isNavigationDisabled={ selectedPreset === null }
					>
						<SidebarView
							onGroupToggle={ handlePluginToggleChange }
							activeTab={ activeTab }
							isSelectedPresetModified={ isSelectedPresetModified }
							featuresView={ pluginSelectionView }
							onFeaturesViewChange={ handlePluginSelectionViewChange }
							onTabChange={ setActiveTabIndex }
						/>
					</DrawerLeft>

					<Box
						sx={ {
							overflow: 'clip',
							padding: 'var(--main-area-padding-vertical) var(--main-area-padding-horizontal)',
							marginTop: APP_BAR_HEIGHT,
							marginLeft: isSidebarOpen ? 'var(--main-area-margin-left)' : 'var(--sidebar-width-closed)',
							transition: theme => theme.transitions.create( 'all', {
								duration: theme.transitions.duration.shorter
							} )
						} }
					>
						{ selectedPreset === null ?
							<EditorPreviewPlaceholder/> :
							<MainView locationOrigin={ locationOrigin } previewLocation={ previewLocation }/>
						}
					</Box>
				</Box>
			</InstallationSettingsContext.Provider>
		</EditorSettingsContext.Provider>
	);
}

function getCalculatedPlugins( editorPlugins: EditorPlugins, toolbarOptions: ToolbarOptions ) {
	const pluginsWithToolbars = getPluginsWithToolbars( editorPlugins, toolbarOptions );
	return getPluginsWithMandatory( pluginsWithToolbars );
}

function getPluginsWithToolbars( editorPlugins: EditorPlugins, toolbarOptions: Partial<ToolbarOptions> ) {
	return [
		...editorPlugins.filter( plugin => plugin !== 'BalloonToolbar' && plugin !== 'BlockToolbar' ),
		...( toolbarOptions.isBalloonToolbarOn ? [ 'BalloonToolbar' ] : [] ),
		...( toolbarOptions.isBlockToolbarOn ? [ 'BlockToolbar' ] : [] )
	];
}

function getPluginsWithMandatory( newPlugins: EditorPlugins ): EditorPlugins {
	return removeDuplicateStrings( MANDATORY_PLUGINS.concat( newPlugins ) );
}
